import { Refresh as RefreshIcon } from '@mui/icons-material';
import { Alert, Box, Button, CircularProgress, Paper, Tab, Tabs, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type React from 'react';
import { ActivityDetailsModal } from './ActivityDashboard/ActivityDetailsModal';
import { ActivityFiltersComponent } from './ActivityDashboard/ActivityFiltersComponent';
import { LoginsTab } from './ActivityDashboard/LoginsTab';
import { OverviewTab } from './ActivityDashboard/OverviewTab';
import { RecentEventsTab } from './ActivityDashboard/RecentEventsTab';
import { StatsCards } from './ActivityDashboard/StatsCards';
import { UsersTab } from './ActivityDashboard/UsersTab';
import { useActivityDashboard } from './ActivityDashboard/useActivityDashboard';
import HistoricalUsagePanel from './HistoricalUsagePanel';

const ActivityDashboard: React.FC = () => {
  const theme = useTheme();

  const {
    activeTab,
    setActiveTab,
    data,
    loading,
    usersList,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    totalCount,
    detailDialogOpen,
    selectedActivity,
    filters,
    handleFilterChange,
    handleSelectChange,
    loadActivityData,
    prepareChartData,
    handleViewDetails,
    handleCloseDetailDialog,
    getUserLabel,
    formatTimestamp,
    getActivityTypeColor,
  } = useActivityDashboard();

  const chartData = prepareChartData();

  if (loading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Activity Dashboard</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
            onClick={loadActivityData}
            disabled={loading}
            sx={{
              backgroundColor: theme.palette.bidlogiq.brick,
              color: theme.palette.common.white,
              fontSize: '1.1rem',
              '&:hover': { backgroundColor: theme.palette.bidlogiq.rust },
            }}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
        </Box>
      </Box>

      <ActivityFiltersComponent
        filters={filters}
        handleFilterChange={handleFilterChange}
        handleSelectChange={handleSelectChange}
        usersList={usersList}
      />

      {data && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Viewing:</strong> {data.scope === 'platform' ? 'Platform-wide' : 'Organization'}{' '}
            activity data
            {data.filters && (
              <span>
                {' '}
                • <strong>Date Range:</strong> {data.filters.startDate} to {data.filters.endDate}
              </span>
            )}
          </Typography>
        </Alert>
      )}

      {data?.data && <StatsCards data={data.data} />}

      <Paper sx={{ mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={(_e, newValue) => {
              setActiveTab(newValue);
              setPage(0);
            }}
          >
            <Tab label="Overview" value="overview" />
            <Tab label="Login Analytics" value="logins" />
            <Tab label="User Activity" value="users" />
            <Tab label="Recent Events" value="recent" />
            <Tab label="Historical Usage" value="historical_usage" />
          </Tabs>
        </Box>

        {activeTab === 'overview' && <OverviewTab chartData={chartData} />}

        {activeTab === 'logins' && data?.data && (
          <LoginsTab data={data.data} chartData={chartData} />
        )}

        {activeTab === 'users' && data?.data && (
          <UsersTab
            data={data.data}
            getUserLabel={getUserLabel}
            formatTimestamp={formatTimestamp}
          />
        )}

        {activeTab === 'recent' && data?.data && (
          <RecentEventsTab
            data={data.data}
            totalCount={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            setPage={setPage}
            setRowsPerPage={setRowsPerPage}
            getUserLabel={getUserLabel}
            formatTimestamp={formatTimestamp}
            getActivityTypeColor={getActivityTypeColor}
            handleViewDetails={handleViewDetails}
          />
        )}

        {activeTab === 'historical_usage' && <HistoricalUsagePanel />}
      </Paper>

      <ActivityDetailsModal
        open={detailDialogOpen}
        onClose={handleCloseDetailDialog}
        selectedActivity={selectedActivity}
        formatTimestamp={formatTimestamp}
        getUserLabel={getUserLabel}
        getActivityTypeColor={getActivityTypeColor}
      />
    </Box>
  );
};

export default ActivityDashboard;
